import { AutocompleteChangeReason, AutocompleteCloseReason } from '@mui/material';
import { filter } from 'lodash';
import { MutableRefObject, SyntheticEvent, useCallback, useMemo, useState } from 'react';

export type HandleSelectOptions = {
  triggerSubmit: boolean;
};

type Args = {
  options: {
    state: string[];
    ref?: MutableRefObject<string[]>;
    update?: (newValue: string[]) => void;
  };
  filterOptions: FilterOption[];
  handleSelect: (newSeriesData: string[], options?: HandleSelectOptions) => void;
};

type Result = {
  open: boolean;
  currentOptions: FilterOption[];
  handleClose: () => void;
  handleOpen: () => void;
  handleClear: () => void;
  handleApply: () => void;
  onChange: (
    _event: SyntheticEvent<Element, Event>,
    value: (string | FilterOption)[],
    reason: AutocompleteChangeReason
  ) => void;
  onCloseAutocomplete: (
    _event: SyntheticEvent<Element, Event>,
    reason: AutocompleteCloseReason
  ) => void;
};

const useCheckboxAutocomplete = (args: Args): Result => {
  const { options, filterOptions, handleSelect } = args;

  const [open, setOpen] = useState(false);
  const currentOptions = useMemo(
    () =>
      filter(filterOptions, (option) => options.state?.includes(option.value)) as FilterOption[],
    [options, filterOptions]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleApply = useCallback(() => {
    if (options.ref) {
      const newValue = options.ref?.current;
      handleSelect?.(newValue, { triggerSubmit: true });
    }
    handleClose();
  }, [options.ref, handleClose, handleSelect]);

  const handleClear = useCallback(() => {
    handleSelect?.([], { triggerSubmit: true });
    handleClose();
  }, [handleSelect, handleClose]);

  const onCloseAutocomplete = (
    _event: SyntheticEvent<Element, Event>,
    reason: AutocompleteCloseReason
  ) => {
    if (reason === 'escape' || reason === 'toggleInput') {
      handleClose();
    }
  };

  const onChange = (
    _event: SyntheticEvent<Element, Event>,
    value: (string | FilterOption)[],
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'clear') {
      handleClear();
    }

    if (reason === 'removeOption' && !open) {
      handleSelect?.(
        (value as FilterOption[]).map((option) => option.value),
        { triggerSubmit: true }
      );
    }
    handleSelect?.((value as FilterOption[]).map((option) => option.value));
  };

  return {
    currentOptions,
    open,
    handleApply,
    handleClear,
    handleClose,
    handleOpen,
    onChange,
    onCloseAutocomplete,
  };
};

export default useCheckboxAutocomplete;
